import React, { useState } from 'react';
import { toast } from 'react-toastify';

const Contact = () => {
  const [formData, setFormData] = useState({
    usn: '',
    name: '',
    email: '',
    reason: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();

    // Validate form
    if (!formData.usn || !formData.name || !formData.email || !formData.reason) {
      toast.error('Please fill in all fields');
      return;
    }

    // Validate email format and cambridge domain
    const emailRegex = /^[^\s@]+@cambridge\.edu\.in$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid Cambridge college email (@cambridge.edu.in)');
      return;
    }

    // Validate USN format (optional - adjust pattern as needed)
    const usnRegex = /^[A-Z0-9]{10}$/i;
    if (!usnRegex.test(formData.usn)) {
      toast.error('Please enter a valid 10-character USN');
      return;
    }

    // Format message for WhatsApp
    const whatsappMessage = `*New Support Request - CITNC ERP*%0A%0A*USN:* ${formData.usn.toUpperCase()}%0A*Name:* ${formData.name}%0A*College Email:* ${formData.email}%0A%0A*Reason:*%0A${formData.reason}`;

    // WhatsApp number
    const whatsappNumber = '916361184577';

    // Open WhatsApp with pre-filled message
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;
    
    window.open(whatsappUrl, '_blank');
    
    toast.success('Opening WhatsApp... Review and send your message!');
    
    // Reset form
    setFormData({
      usn: '',
      name: '',
      email: '',
      reason: ''
    });
  };

  return (
    <div className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-green-50 to-emerald-100 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Contact Us</h1>
          <p className="text-lg text-slate-600">Get in touch with us via WhatsApp. We'll respond as soon as possible!</p>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* WhatsApp Contact Form */}
          <div className="rounded-3xl p-8 md:p-10 shadow-xl bg-white" style={{ border: '3px solid #25D366' }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#25D366' }}>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  className="w-6 h-6 text-white"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              </div>
              <h3 className="text-2xl font-bold" style={{ color: '#25D366' }}>Connect via WhatsApp</h3>
            </div>

            <p className="text-sm mb-6 text-slate-600">
              Fill in your details below and we'll open WhatsApp with your message ready to send!
            </p>

            <form onSubmit={handleWhatsAppSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2 text-slate-900">
                  USN <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="usn"
                  value={formData.usn}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl focus:outline-none transition-all border-2 border-green-300 focus:border-green-500 uppercase"
                  placeholder="1XX21CS999"
                  maxLength="10"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-slate-900">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl focus:outline-none transition-all border-2 border-green-300 focus:border-green-500"
                  placeholder="Your Full Name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-slate-900">
                  College Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl focus:outline-none transition-all border-2 border-green-300 focus:border-green-500"
                  placeholder="yourusn@cambridge.edu.in"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-slate-900">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="reason"
                  rows="5"
                  value={formData.reason}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl focus:outline-none transition-all resize-none border-2 border-green-300 focus:border-green-500"
                  placeholder="Tell us your reason for contacting support..."
                  required
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full group relative px-8 py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-3"
                style={{ backgroundColor: '#25D366', color: 'white' }}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  className="w-6 h-6 group-hover:rotate-12 transition-transform"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                <span>Continue to WhatsApp</span>
              </button>

              <p className="text-xs text-center mt-4 text-slate-500">
                Your message will open in WhatsApp. You can review it before sending.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
